/**
 * UI-37: Action Execution — Close the Loop
 *
 * State-driven action execution lifecycle:
 *   idle → executing → awaiting-confirmation → completed
 *
 * Every action executes against the real backend:
 *   click → POST /ops/actions/execute → backend state mutation → events emitted
 *   → pipeline reacts → control tower updates → action disappears
 *
 * Rules:
 *   - Every click produces visible feedback (no silent actions)
 *   - Duplicate clicks prevented while executing
 *   - Errors keep the action available for retry (P7)
 *   - Polling never overwrites in-flight execution state (P8)
 *   - Single ActionResult model drives both toast and card (P2)
 *   - NO artificial delays — completion is state-driven
 *   - NO fake success — backend result drives the UI
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Action, ActionCTA } from '../services/action-engine/actionTypes';
import { executeActionOnBackend } from '../services/actionExecutionService';
import type { ActionExecutionResult, MetricsSnapshot, ActionMutation } from '../services/actionExecutionService';

// ─── ActionResult — Single Source of Truth (P2) ──────────────────────────────

export type ActionResultStatus = 'success' | 'warning' | 'error';

export interface ActionResult {
  readonly status: ActionResultStatus;
  readonly message: string;
  readonly source: 'system' | 'ui';
}

// ─── Execution phase for each action ─────────────────────────────────────────

export type ExecutionPhase = 'executing' | 'awaiting-confirmation' | 'completed';

export interface ActionExecutionEntry {
  readonly phase: ExecutionPhase;
  readonly result?: ActionResult;
  /** Timestamp when the phase was entered (for completion display timing) */
  readonly enteredAt: number;
}

// ─── Toast State ─────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'warning' | 'error';

export interface ActionToastState {
  readonly id: string;
  readonly message: string;
  readonly variant: ToastVariant;
}

// ─── CTA → Operational Verb (P2 — replaces "✓ Done") ────────────────────────

const CTA_COMPLETION_VERB: Record<string, string> = {
  dispatch: 'Dispatched',
  assign: 'Assigned',
  escalate: 'Escalated',
  // OPS-38.2: distinct verbs preserve operational truth in toasts — a
  // 'recover' action must NOT report "escalated".
  prioritize: 'Prioritized',
  recover: 'Recovery dispatched',
  navigate: 'Opened',
};

export function completionVerb(cta: ActionCTA): string {
  return CTA_COMPLETION_VERB[cta.type] ?? 'Completed action';
}

// ─── CTA → Feedback ActionResult ─────────────────────────────────────────────

function buildActionResultFromBackend(backendResult: ActionExecutionResult): ActionResult {
  return {
    status: backendResult.success ? 'success' : 'error',
    message: backendResult.message,
    source: 'system',
  };
}

// ─── Constants ──────────────────────────────────────────────────────────────

const TOAST_DURATION_MS = 4_000;
const COMPLETED_DISPLAY_MS = 3_000;

// ─── Hook ───────────────────────────────────────────────────────────────────

interface UseActionExecutionOptions {
  /** Called after successful action to trigger data refresh */
  onRefresh?: () => void;
}

export function useActionExecution(options: UseActionExecutionOptions = {}) {
  const navigate = useNavigate();
  const [executionMap, setExecutionMap] = useState<Map<string, ActionExecutionEntry>>(new Map());
  const [toast, setToast] = useState<ActionToastState | null>(null);
  const [lastExecution, setLastExecution] = useState<{ beforeSnapshot: MetricsSnapshot; afterSnapshot: MetricsSnapshot; mutations?: readonly ActionMutation[] } | null>(null);
  // UI-43: Track the last action ID that produced mutatedCount === 0
  const [lastNoOpActionId, setLastNoOpActionId] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const cleanupTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  /** Action IDs with in-flight execution — polling must not overwrite these (P8) */
  const executionGuardRef = useRef<Set<string>>(new Set());
  /** Tracks mount state to prevent state updates after unmount (M2) */
  const isMountedRef = useRef(true);
  /** Stable ref for onRefresh — avoids inline object in useCallback deps */
  const onRefreshRef = useRef(options.onRefresh);
  onRefreshRef.current = options.onRefresh;

  useEffect(() => {
    isMountedRef.current = true; // Reset after StrictMode remount cycle
    return () => {
      isMountedRef.current = false;
      clearTimeout(toastTimerRef.current);
      for (const timer of cleanupTimersRef.current.values()) {
        clearTimeout(timer);
      }
    };
  }, []);

  const showToast = useCallback((message: string, variant: ToastVariant) => {
    if (!isMountedRef.current) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setToast({ id, message, variant });
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) setToast(null);
    }, TOAST_DURATION_MS);
  }, []);

  const dismissToast = useCallback(() => {
    clearTimeout(toastTimerRef.current);
    setToast(null);
  }, []);

  const transitionToCompleted = useCallback((actionId: string, result: ActionResult) => {
    if (!isMountedRef.current) return;
    setExecutionMap(prev => {
      const next = new Map(prev);
      next.set(actionId, { phase: 'completed', result, enteredAt: Date.now() });
      return next;
    });

    const timer = setTimeout(() => {
      if (!isMountedRef.current) return;
      setExecutionMap(prev => {
        const next = new Map(prev);
        next.delete(actionId);
        return next;
      });
      executionGuardRef.current.delete(actionId);
      cleanupTimersRef.current.delete(actionId);
    }, COMPLETED_DISPLAY_MS);
    cleanupTimersRef.current.set(actionId, timer);
  }, []);

  const executeAction = useCallback(async (action: Action) => {
    if (executionGuardRef.current.has(action.id)) return;

    // ── Phase 1: executing ──────────────────────────────────────────
    executionGuardRef.current.add(action.id);
    setExecutionMap(prev => {
      const next = new Map(prev);
      next.set(action.id, { phase: 'executing', enteredAt: Date.now() });
      return next;
    });

    try {
      // Micro-yield: ensures Phase 1 renders before transitioning (C2 fix)
      await Promise.resolve();

      if (!isMountedRef.current) {
        executionGuardRef.current.delete(action.id);
        return;
      }

      // ── Navigate-only actions skip backend execution ──────────────
      if (action.cta.type === 'navigate') {
        const result: ActionResult = { status: 'success', message: 'Navigating…', source: 'ui' };
        transitionToCompleted(action.id, result);
        showToast(result.message, 'success');
        navigate(action.cta.to);
        return;
      }

      // ── Phase 2: awaiting-confirmation — execute against backend ──
      setExecutionMap(prev => {
        const next = new Map(prev);
        next.set(action.id, { phase: 'awaiting-confirmation', enteredAt: Date.now() });
        return next;
      });

      // Execute real backend mutation (navigate actions already returned above)
      const ctaPayload = action.cta.payload as { target?: string; action?: string } | undefined;

      const backendResult = await executeActionOnBackend({
        actionId: action.id,
        ctaType: action.cta.type as 'dispatch' | 'assign' | 'escalate' | 'prioritize' | 'recover',
        domain: action.domain,
        payload: ctaPayload ?? undefined,
      });

      if (!isMountedRef.current) {
        executionGuardRef.current.delete(action.id);
        return;
      }

      // ── Phase 3: completed — result from real backend ─────────────
      // UI-43 / PILOT-21: detect no-op BEFORE building ActionResult so the
      // completion state and toast variant correctly reflect "nothing happened"
      // rather than showing a misleading green success for a no-op action.
      const isNoOp = backendResult.execution.mutatedCount === 0;
      const result: ActionResult = isNoOp
        ? { status: 'warning', message: backendResult.message, source: 'system' }
        : buildActionResultFromBackend(backendResult);
      transitionToCompleted(action.id, result);
      showToast(result.message, isNoOp ? 'warning' : result.status === 'success' ? 'success' : 'error');

      // Preserve snapshot data for ExecutionImpactInline (UI-41 / PILOT-23)
      if (backendResult.execution?.beforeSnapshot && backendResult.execution?.afterSnapshot) {
        setLastExecution({
          beforeSnapshot: backendResult.execution.beforeSnapshot,
          afterSnapshot: backendResult.execution.afterSnapshot,
          mutations: backendResult.execution.mutations,
        });
      }

      // PILOT-23: enrich toast with specific room numbers from mutations
      if (!isNoOp && backendResult.execution?.mutations?.length > 0) {
        const rooms = [...new Set(
          backendResult.execution.mutations
            .map((m: ActionMutation) => m.display?.roomNumber)
            .filter(Boolean)
        )];
        if (rooms.length > 0) {
          const verb = completionVerb(action.cta);
          const roomList = rooms.slice(0, 5).join(', ');
          const suffix = rooms.length > 5 ? ` +${rooms.length - 5} more` : '';
          showToast(`${verb}: Room ${roomList}${suffix}`, result.status === 'success' ? 'success' : 'error');
        }
      }

      // UI-43: Suppress no-op action from queue after amber warning is shown
      if (isNoOp) {
        setLastNoOpActionId(action.id);
      } else {
        setLastNoOpActionId(null);
      }

      // Trigger data refresh — pipeline has already reacted on the backend
      onRefreshRef.current?.();
    } catch (err) {
      // ── Error: keep action in queue for retry (P7) ────────────────
      console.error('[ACTION_EXECUTION] Failed:', action.id, err);
      if (isMountedRef.current) {
        setExecutionMap(prev => {
          const next = new Map(prev);
          next.delete(action.id);
          return next;
        });
      }
      executionGuardRef.current.delete(action.id);
      showToast('Action failed — try again', 'error');
    }
  }, [navigate, showToast, transitionToCompleted]);

  const isExecuting = useCallback(
    (actionId: string): boolean => {
      const entry = executionMap.get(actionId);
      return entry?.phase === 'executing' || entry?.phase === 'awaiting-confirmation';
    },
    [executionMap],
  );

  const isCompleted = useCallback(
    (actionId: string): boolean => executionMap.get(actionId)?.phase === 'completed',
    [executionMap],
  );

  const getResult = useCallback(
    (actionId: string): ActionResult | undefined => executionMap.get(actionId)?.result,
    [executionMap],
  );

  /** IDs currently in execution — polling must preserve these states (P8) */
  const getExecutionGuardIds = useCallback(
    (): ReadonlySet<string> => executionGuardRef.current,
    [],
  );

  return {
    executeAction,
    isExecuting,
    isCompleted,
    getResult,
    getExecutionGuardIds,
    toast,
    dismissToast,
    executionMap: executionMap as ReadonlyMap<string, ActionExecutionEntry>,
    lastExecution,
    lastNoOpActionId,
  };
}
